import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { updatePageMeta } from '../../utils/seo';
import { DOMAIN_CONFIG } from '../../config/domain';
import Card from '../../components/UI/Card';

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    updatePageMeta({
      title: 'Privacy Policy',
      description: 'Learn how πPi Dating protects your privacy and handles your personal data. Comprehensive privacy policy for our Pi Network dating app.',
      keywords: 'privacy policy, data protection, pi dating privacy, personal information',
      url: `https://${DOMAIN_CONFIG.domain}/privacy-policy`
    });
  }, []);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings/privacy-security')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Privacy Policy</h1>
      </div>

      <div className="px-4 pb-8">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield size={32} className="text-purple-600 dark:text-purple-400" />
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">πPi Dating Privacy Policy</h2>
              <p className="text-gray-600 dark:text-gray-300">Last updated: {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          <div className="space-y-6 text-gray-700 dark:text-gray-300">
            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">1. Information We Collect</h3>
              <div className="space-y-2">
                <p><strong>Profile Information:</strong> Name, age, photos, bio, interests, and location data to help you connect with compatible matches.</p>
                <p><strong>Pi Network Data:</strong> Your Pi Network username and wallet information for payment processing and verification.</p>
                <p><strong>Usage Data:</strong> How you interact with the app, including swipes, matches, and messages to improve our matching algorithm.</p>
                <p><strong>Device Information:</strong> Device type, operating system, and app version for technical support and optimization.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">2. How We Use Your Information</h3>
              <div className="space-y-2">
                <p>• <strong>Matching:</strong> To show you potential matches based on your preferences and location</p>
                <p>• <strong>Communication:</strong> To enable messaging between matched users</p>
                <p>• <strong>Safety:</strong> To verify profiles and prevent fraud or inappropriate behavior</p>
                <p>• <strong>Payments:</strong> To process Pi Network transactions for premium features</p>
                <p>• <strong>Improvement:</strong> To enhance our matching algorithm and user experience</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">3. Information Sharing</h3>
              <div className="space-y-2">
                <p><strong>With Other Users:</strong> Your profile information is visible to other users based on your privacy settings.</p>
                <p><strong>Pi Network:</strong> We share necessary transaction data with Pi Network for payment processing.</p>
                <p><strong>Service Providers:</strong> We may share data with trusted third-party services for app functionality and security.</p>
                <p><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect user safety.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">4. Data Security</h3>
              <div className="space-y-2">
                <p>We implement industry-standard security measures to protect your personal information:</p>
                <p>• End-to-end encryption for messages</p>
                <p>• Secure data storage with regular backups</p>
                <p>• Regular security audits and updates</p>
                <p>• Limited access to personal data by authorized personnel only</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">5. Your Privacy Rights</h3>
              <div className="space-y-2">
                <p><strong>Access:</strong> You can view and download your personal data at any time.</p>
                <p><strong>Correction:</strong> You can update or correct your profile information.</p>
                <p><strong>Deletion:</strong> You can delete your account and personal data permanently.</p>
                <p><strong>Portability:</strong> You can export your data in a machine-readable format.</p>
                <p><strong>Opt-out:</strong> You can opt out of data sharing and marketing communications.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">6. Contact Us</h3>
              <div className="space-y-2">
                <p>If you have questions about this privacy policy or your personal data, please contact us:</p>
                <p>• Email: <a href={`mailto:${DOMAIN_CONFIG.email.privacy}`} className="text-purple-600 dark:text-purple-400 hover:underline">{DOMAIN_CONFIG.email.privacy}</a></p>
                <p>• Support: <a href={`mailto:${DOMAIN_CONFIG.email.support}`} className="text-purple-600 dark:text-purple-400 hover:underline">{DOMAIN_CONFIG.email.support}</a></p>
                <p>• Website: <a href={`https://${DOMAIN_CONFIG.domain}`} className="text-purple-600 dark:text-purple-400 hover:underline">{DOMAIN_CONFIG.domain}</a></p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-700">
              <p className="text-sm text-purple-800 dark:text-purple-200">
                <strong>Your Privacy Matters:</strong> At πPi Dating, we're committed to protecting your privacy and giving you control over your personal information. We believe in transparency and will always be clear about how we collect, use, and protect your data.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;